import React, { useReducer, useEffect, useState } from "react";
import { Routes, Route, useParams, useNavigate, Outlet } from "react-router-dom";
import ExchangeSubscriptionsList from "./ExchangeSubscriptionList";
import ExchangeReason from "./ExchangeReason";
import CurrentVehicle from "./CurrentVehicle";
import NewVehicle from "./NewVehicle";
import { currentVehicleReducer, currentVehicleInitialState } from "./currentVehicleDetailsReducer";
import { ExchangeTypes, exchangeReasonInitialState, exchangeReasonReducer } from "./exchangeReasonReducer";
import PriceChangePlan from "./PriceChangePlan";
import PriceChangeConfirmation from "./PriceChangeConfirmation";
import { priceChangeInitialState, priceChangeReducer } from "./priceChangeReducer";
import { defaultNewVehicleState, newVehicleReducer } from "./newVehicleReducer";
import ExchangeReview from "./ExchangeReview";
import { selectedSubscriptionInitialState, selectedSubscriptionReducer } from "./selectedSubscriptionReducer";
import AdditionalCharges from "./AdditionalCharges";
import { additionalChargesInitialState, additionalChargesReducer } from "./additionalChargesReducer";
import Warning from "@components/modal/info/Warning";
import { Country } from "@utils/constants/localisation";

// Enum for vehicle exchange journey paths
export enum VehicleExchangePath {
  Root = "",
  ExchangeReason = "exchange-reason",
  CurrentVehicle = "current-vehicle",
  AdditionalCharges = "additional-charges",
  NewVehicle = "new-vehicle",
  // Price change only - sit between NewVehicle and Review
  PriceChangePlan = "price-change-plan",
  PriceChangeConfirmation = "price-change-confirmation",
  Review = "review"
}

export default function ExchangeParent() {
  const { custId, country } = useParams();
  const navigate = useNavigate();

  const [selectedSubscriptionState, selectedSubscriptionDispatch] = useReducer(selectedSubscriptionReducer, selectedSubscriptionInitialState);
  const [currentVehicleState, currentVehicleDispatch] = useReducer(currentVehicleReducer, currentVehicleInitialState);
  const [exchangeReasonState, exchangeReasonDispatch] = useReducer(exchangeReasonReducer, exchangeReasonInitialState);
  const [additionalChargesState, additionalChargesDispatch] = useReducer(additionalChargesReducer, additionalChargesInitialState);
  const [newVehicleState, newVehicleDispatch] = useReducer(newVehicleReducer, defaultNewVehicleState)
  const [priceChangeState, priceChangeDispatch] = useReducer(priceChangeReducer, priceChangeInitialState);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Unknown error");

  const isPriceChange = exchangeReasonState.exchangeType === ExchangeTypes.PriceChange;

  function buildVehicleExchangePath(targetComponent: VehicleExchangePath) {
    return `/vehicle-exchange/${country}/${custId}/${targetComponent}`;
  }

  // Target after new vehicle - depends on if the exchange type is a pricechange
  const afterNewVehicleTarget = isPriceChange
    ? buildVehicleExchangePath(VehicleExchangePath.PriceChangePlan)
    : buildVehicleExchangePath(VehicleExchangePath.Review);

  // Validate all required states up to and including the current route
  function validateJourneyState(window_path: string): string | null {
    const isOnStep = (...steps: VehicleExchangePath[]) =>
      steps.some(step => window_path.endsWith(`/${step}`));

    // Always check subscription for all steps except select-sub
    if (
      isOnStep(
        VehicleExchangePath.ExchangeReason,
        VehicleExchangePath.CurrentVehicle,
        VehicleExchangePath.AdditionalCharges,
        VehicleExchangePath.NewVehicle,
        VehicleExchangePath.PriceChangePlan,
        VehicleExchangePath.PriceChangeConfirmation,
        VehicleExchangePath.Review,
      )
    ) {
      if (!selectedSubscriptionState.selectedSubsId) return "No selected subscription found.";
    }
    // Check exchange reason for all steps after exchange-reason
    if (
      isOnStep(
        VehicleExchangePath.CurrentVehicle,
        VehicleExchangePath.AdditionalCharges,
        VehicleExchangePath.NewVehicle,
        VehicleExchangePath.PriceChangePlan,
        VehicleExchangePath.PriceChangeConfirmation,
        VehicleExchangePath.Review,
      )
    ) {
      if (!exchangeReasonState.exchangeReason || exchangeReasonState.exchangeReason.trim().length === 0)
        return "No exchange reason found.";
    }
    // Check current vehicle for all steps after current-vehicle
    if (
      isOnStep(
        VehicleExchangePath.AdditionalCharges,
        VehicleExchangePath.NewVehicle,
        VehicleExchangePath.PriceChangePlan,
        VehicleExchangePath.PriceChangeConfirmation,
        VehicleExchangePath.Review,
      )
    ) {
      if (!currentVehicleState.vehicle || !currentVehicleState.vehicle.id || currentVehicleState.vehicle.id.trim().length === 0)
        return "No current vehicle found.";
      // GB captures where the returning vehicle is being left; AU has no equivalent Fleetio
      // field, so only GB journeys are bounced back for it (TP-6849).
      if (country === Country.GB &&
        (!currentVehicleState.returnLocation || currentVehicleState.returnLocation.trim().length === 0))
        return "No vehicle return location found.";
    }
    // Check new vehicle for all steps after new-vehicle
    if (
      isOnStep(
        VehicleExchangePath.PriceChangePlan,
        VehicleExchangePath.PriceChangeConfirmation,
        VehicleExchangePath.Review,
      )
    ) {
      if (!newVehicleState.selectedVehicle || !newVehicleState.selectedVehicle.id || newVehicleState.selectedVehicle.id.trim().length === 0)
        return "No new vehicle found.";
    }
    // The price change steps only apply to that exchange type, and only gate each other and the review
    if (isPriceChange) {
      if (isOnStep(VehicleExchangePath.PriceChangeConfirmation, VehicleExchangePath.Review)) {
        if (!priceChangeState.newPlanId) return "No new plan found.";
      }
      if (isOnStep(VehicleExchangePath.Review)) {
        if (!priceChangeState.priceConfirmed) return "New price has not been confirmed.";
      }
    }
    return null;
  }

  // Validate state is set, when the path changes
  useEffect(() => {
    // Use pathname for route matching
    const error = validateJourneyState(window.location.hash);
    if (error) {
      setErrorMessage(error);
      setShowErrorModal(true);
    } else {
      setShowErrorModal(false);
      setErrorMessage("");
    }
  }, [window.location.pathname]);

  function handleStateErrorModalClose() {
    // If state error, sends the user back to the start of the journey
    setShowErrorModal(false);
    navigate(buildVehicleExchangePath(VehicleExchangePath.Root)); // Go (back) to start
  }

  return (
    <>
      {showErrorModal && (
        <Warning
          message={errorMessage}
          additionalInfo={"The Vehicle Exchange Journey has encountered an error. Missing state from a previous step. You must restart the journey."}
          onClickClose={handleStateErrorModalClose}
        />
      )}
      <Routes>
        <Route
          path={VehicleExchangePath.Root}
          element={
            <ExchangeSubscriptionsList
              subscriptionStateDispatch={selectedSubscriptionDispatch}
              subscriptionState={selectedSubscriptionState}
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.ExchangeReason)}
            />
          }
        />
        <Route
          path={VehicleExchangePath.ExchangeReason}
          element={
            <ExchangeReason
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.CurrentVehicle)}
              exchangeReasonState={exchangeReasonState}
              exchangeReasonDispatch={exchangeReasonDispatch}
              selectedSubscriptionState={selectedSubscriptionState}
            />
          }
        />
        <Route
          path={VehicleExchangePath.CurrentVehicle}
          element={
            <CurrentVehicle
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.AdditionalCharges)}
              selectedSubscriptionState={selectedSubscriptionState}
              currentVehicleState={currentVehicleState}
              currentVehicleDispatcher={currentVehicleDispatch}
              exchangeReasonState={exchangeReasonState}
            />
          }
        />
        <Route
          path={VehicleExchangePath.AdditionalCharges}
          element={
            <AdditionalCharges
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.NewVehicle)}
              currentVehicleState={currentVehicleState}
              additionalChargesState={additionalChargesState}
              additionalChargesDispatch={additionalChargesDispatch}
            />
          }
        />
        <Route
          path={VehicleExchangePath.NewVehicle}
          element={
            <NewVehicle
              navigateTarget={afterNewVehicleTarget}
              selectedSubscriptionState={selectedSubscriptionState}
              currentVehicleState={currentVehicleState}
              newVehicleState={newVehicleState}
              newVehicleDispatcher={newVehicleDispatch}
              exchangeReasonState={exchangeReasonState}
            />
          }
        />
        {isPriceChange && (
          <Route
            path={VehicleExchangePath.PriceChangePlan}
            element={
              <PriceChangePlan
                navigateTarget={buildVehicleExchangePath(VehicleExchangePath.PriceChangeConfirmation)}
                selectedSubscriptionState={selectedSubscriptionState}
                currentVehicleState={currentVehicleState}
                priceChangeState={priceChangeState}
                priceChangeDispatch={priceChangeDispatch}
              />
            }
          />
        )}
        {isPriceChange && (
          <Route
            path={VehicleExchangePath.PriceChangeConfirmation}
            element={
              <PriceChangeConfirmation
                navigateTarget={buildVehicleExchangePath(VehicleExchangePath.Review)}
                priceChangeState={priceChangeState}
                priceChangeDispatch={priceChangeDispatch}
              />
            }
          />
        )}
        <Route
          path={VehicleExchangePath.Review}
          element={
            <ExchangeReview
              selectedSubscriptionState={selectedSubscriptionState}
              currentVehicleState={currentVehicleState}
              newVehicleState={newVehicleState}
              exchangeReasonState={exchangeReasonState}
              additionalChargesState={additionalChargesState}
              priceChangeState={priceChangeState}
            />
          }
        />
      </Routes>
      <Outlet />
    </>
  );
}
